import React, { useCallback, useEffect, useMemo, useState } from "react";
import { appointmentService, googleCalendarService } from "../services/dataService";
import { getRoles } from "../services/api";
import { usePermissions } from "../context/PermissionContext";
import { useSettings } from "../context/SettingsContext";
import {
  Appointment,
  AppointmentCalendar,
  AppointmentType,
  AvailabilityExceptionRow,
  CalendarBlock,
  StaffAvailabilityRow,
  StaffBookingProfile,
  GoogleAccountStatus,
  AppointmentPeople,
} from "../types";
import "./Appointments.css";

/**
 * Randevu ve Takvim (appointments modülü).
 *
 * Dört sekme: Takvim · Onay bekleyenler · Çalışma saatleri · Randevu türleri.
 * Saatler her yerde TSİ; sunucu UTC gönderir, gösterim tarayıcının yerel
 * saatiyle yapılır ve başlıkta bunun TSİ olduğu açıkça yazılır.
 */

const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const SHORT = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00 – 19:00

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const startOfWeek = (d: Date) => {
  const x = new Date(d);
  const wd = (x.getDay() + 6) % 7; // Pazartesi = 0
  x.setDate(x.getDate() - wd);
  x.setHours(0, 0, 0, 0);
  return x;
};

const hhmm = (s: string) =>
  new Date(s).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

const dayLabel = (d: Date) =>
  d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });

/**
 * datetime-local alanı YEREL saat ister, sunucu UTC bekler. Çevrim tek yerde
 * dursun: doğrudan toISOString().slice(0,16) yazmak saati TSİ farkı kadar
 * kaydırır ve meşguliyet yanlış saate düşer.
 */
const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (s: string) => new Date(s).toISOString();

type Tab = "calendar" | "pending" | "hours" | "types";

const Appointments: React.FC = () => {
  const { has } = usePermissions();
  const { settings } = useSettings();
  const canSettings = has("appointments:settings");
  const canUpdate = has("appointments:update");

  const [tab, setTab] = useState<Tab>("calendar");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  // Kimin takvimi: "me" | "team" (İK'da bana bağlı ekip + ben) | kullanıcı kimliği
  const [who, setWho] = useState<string>("me");
  const [people, setPeople] = useState<AppointmentPeople | null>(null);
  useEffect(() => { appointmentService.people().then(setPeople).catch(() => setPeople(null)); }, []);
  const [cal, setCal] = useState<AppointmentCalendar>({ appointments: [], blocks: [] });
  const [pending, setPending] = useState<Appointment[]>([]);
  const [detail, setDetail] = useState<Appointment | null>(null);

  const [profile, setProfile] = useState<StaffBookingProfile | null>(null);
  const [hours, setHours] = useState<StaffAvailabilityRow[]>([]);
  const [exceptions, setExceptions] = useState<AvailabilityExceptionRow[]>([]);
  const [types, setTypes] = useState<AppointmentType[]>([]);
  const [roles, setRoles] = useState<{ roleId: number; name: string; userCount?: number }[]>([]);
  const [editType, setEditType] = useState<AppointmentType | null>(null);
  const [blockDraft, setBlockDraft] = useState<CalendarBlock | null>(null);
  const [google, setGoogle] = useState<GoogleAccountStatus | null>(null);

  const flash = (m: string) => {
    setOk(m);
    window.setTimeout(() => setOk(null), 3000);
  };
  const fail = (e: any, fallback: string) =>
    setErr(e?.response?.data?.message || e?.message || fallback);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart],
  );

  const loadCalendar = useCallback(async () => {
    const from = new Date(weekStart);
    const to = new Date(weekStart);
    to.setDate(to.getDate() + 7);
    try {
      setCal(await appointmentService.calendar(from.toISOString(), to.toISOString(), who === "me" || who === "team" ? undefined : who, who === "team" ? "team" : undefined));
    } catch (e) {
      fail(e, "Takvim yüklenemedi.");
    }
  }, [weekStart, who]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, h, t, pend] = await Promise.all([
          appointmentService.profile(),
          appointmentService.availability(),
          appointmentService.types(),
          appointmentService.pending(),
        ]);
        setProfile(p);
        setHours(h);
        setTypes(t);
        setPending(pend);
        if (canSettings) {
          try {
            setRoles((await getRoles()).data ?? []);
          } catch {
            /* rol listesi alınamazsa tür ekranı yine çalışır */
          }
        }
      } catch (e) {
        fail(e, "Randevu verileri yüklenemedi.");
      } finally {
        setLoading(false);
      }
    })();
  }, [canSettings]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // Google bağlantı durumu + dönüş sonucu. Google bizi ?google=... ile geri
  // gönderiyor; sonucu bir kez gösterip parametreyi adres çubuğundan siliyoruz
  // ki sayfa yenilendiğinde eski mesaj tekrar çıkmasın.
  useEffect(() => {
    googleCalendarService.status().then(setGoogle).catch(() => {});

    const p = new URLSearchParams(window.location.search).get("google");
    if (!p) return;
    const msg: Record<string, string> = {
      baglandi: "Google Takvim bağlandı. Onayladığınız randevular takviminize düşecek.",
      iptal: "Google bağlantısı iptal edildi.",
      hata: "Google bağlantısı kurulamadı. Lütfen tekrar deneyin.",
      eksik: "Google'dan eksik yanıt geldi. Lütfen tekrar deneyin.",
      gecersiz: "Bağlantı isteğinin süresi dolmuş. Lütfen tekrar deneyin.",
    };
    if (p === "baglandi") flash(msg[p]);
    else setErr(msg[p] ?? "Google bağlantısı tamamlanamadı.");
    setTab("hours");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!profile) return;
    const from = iso(new Date());
    const to = iso(new Date(Date.now() + 90 * 864e5));
    appointmentService.exceptions(undefined, from, to).then(setExceptions).catch(() => {});
  }, [profile]);

  // ---- Takvim yerleşimi ----

  const eventsFor = (day: Date) => {
    const key = iso(day);
    const apts = cal.appointments
      .filter((a) => iso(new Date(a.startAt)) === key && a.status !== 4 && a.status !== 5)
      .map((a) => ({ kind: "apt" as const, a }));
    const blocks = cal.blocks
      .filter((b) => iso(new Date(b.startAt)) === key)
      .map((b) => ({ kind: "block" as const, b }));
    return [...apts, ...blocks].sort((x, y) =>
      (x.kind === "apt" ? x.a.startAt : x.b.startAt).localeCompare(
        y.kind === "apt" ? y.a.startAt : y.b.startAt,
      ),
    );
  };

  /**
   * Kurulum listesi. Her adım GERÇEK duruma bakar, kullanıcının "yaptım" demesine
   * değil: yarım kalmış bir kurulumda çalışan bağlantısını paylaşıp randevu
   * gelmeyince nedenini bulamıyordu.
   */
  const myTypes = types.filter(
    (t) => profile?.myRoleId != null && t.roleIds.includes(profile.myRoleId) && t.isPublic,
  );
  const setupSteps = [
    {
      title: "Randevu sayfanızı oluşturun",
      done: !!profile?.exists,
      todo: "Aşağıdaki “Randevu sayfanız” bölümünü doldurup kaydedin. Bağlantı adı adresinizin sonundaki kısımdır.",
      doneText: profile?.publicUrl ?? "Kaydedildi.",
    },
    {
      title: "Çalışma saatlerinizi girin",
      done: hours.length > 0,
      todo: "Hangi gün ve saatlerde randevu kabul ettiğinizi işaretleyin. Saat girilmeyen günde randevu alınamaz.",
      doneText: `${hours.length} gün açık.`,
    },
    {
      title: "Rolünüze randevu türü tanımlı olsun",
      done: myTypes.length > 0,
      todo:
        profile?.myRoleId == null
          ? "Kullanıcınıza rol atanmamış görünüyor. Yöneticinizden rol tanımlamasını isteyin."
          : "Türler role bağlanır ve yalnız yetkili kişi tanımlar. Yöneticinizden rolünüze en az bir randevu türü işaretlemesini isteyin.",
      doneText: myTypes.map((t) => t.name).join(", "),
    },
    ...(google?.configured
      ? [
          {
            title: "Google Takvim’e bağlanın",
            done: !!google?.connected && !google?.lastError,
            todo:
              "Bağlarsanız her randevu takviminize düşer ve o randevuya özel Meet bağlantısı üretilir. Bağlamazsanız profilinizdeki sabit oda kullanılır.",
            doneText: google?.googleEmail ?? "Bağlı.",
          },
        ]
      : []),
    {
      title: "Sayfanızı yayına alın",
      done: !!profile?.isPublic && !!settings?.publicBookingEnabled,
      todo: !settings?.publicBookingEnabled
        ? "Genel randevu sayfası sistem genelinde kapalı. Yöneticinizden Ayarlar › Randevu Sayfası bölümünden açmasını isteyin."
        : "Aşağıdaki “Sayfam yayında olsun” anahtarını açın.",
      doneText: "Sayfanız dışarıdan açılabiliyor.",
    },
  ];

  const eventsAt = (day: Date, hour: number) =>
    eventsFor(day).filter((e) => {
      const s = new Date(e.kind === "apt" ? e.a.startAt : e.b.startAt);
      return s.getHours() === hour;
    });

  // ---- İşlemler ----

  const decide = async (a: Appointment, approve: boolean) => {
    if (!approve) {
      const reason = window.prompt("Ret gerekçesi (misafire iletilir, boş bırakılabilir):", "");
      if (reason === null) return;
      try {
        await appointmentService.reject(a.appointmentId, reason);
        flash("Talep reddedildi, misafire bildirildi.");
      } catch (e) {
        return fail(e, "Ret işlemi başarısız.");
      }
    } else {
      try {
        await appointmentService.approve(a.appointmentId);
        flash("Randevu onaylandı, misafire takvim daveti gönderildi.");
      } catch (e) {
        return fail(e, "Onay işlemi başarısız.");
      }
    }
    setDetail(null);
    setPending(await appointmentService.pending());
    loadCalendar();
  };

  const cancelAppointment = async (a: Appointment) => {
    const reason = window.prompt("İptal gerekçesi (misafire iletilir):", "");
    if (reason === null) return;
    try {
      await appointmentService.setStatus(a.appointmentId, 5, reason);
      flash("Randevu iptal edildi, misafire bildirildi.");
      setDetail(null);
      setPending(await appointmentService.pending());
      loadCalendar();
    } catch (e) {
      fail(e, "İptal başarısız.");
    }
  };

  const markStatus = async (a: Appointment, status: 6 | 7) => {
    try {
      await appointmentService.setStatus(a.appointmentId, status);
      flash(status === 6 ? "Tamamlandı olarak işaretlendi." : "Gelinmedi olarak işaretlendi.");
      setDetail(null);
      loadCalendar();
    } catch (e) {
      fail(e, "İşlem başarısız.");
    }
  };

  const saveProfile = async () => {
    if (!profile) return;
    try {
      const r = await appointmentService.saveProfile(profile);
      setProfile({ ...profile, slug: r.slug, publicUrl: r.publicUrl });
      flash("Randevu sayfanız kaydedildi.");
    } catch (e) {
      fail(e, "Profil kaydedilemedi.");
    }
  };

  const saveHours = async () => {
    try {
      await appointmentService.saveAvailability(hours);
      flash("Çalışma saatleri kaydedildi.");
      loadCalendar();
    } catch (e) {
      fail(e, "Çalışma saatleri kaydedilemedi.");
    }
  };

  const setDayHours = (dow: number, on: boolean) => {
    setHours((prev) =>
      on
        ? [...prev, { dayOfWeek: dow, startTime: "09:00", endTime: "18:00" }]
        : prev.filter((h) => h.dayOfWeek !== dow),
    );
  };

  const patchDay = (dow: number, patch: Partial<StaffAvailabilityRow>) =>
    setHours((prev) => prev.map((h) => (h.dayOfWeek === dow ? { ...h, ...patch } : h)));

  const addException = async () => {
    const date = window.prompt("Kapatılacak gün (YYYY-AA-GG):", iso(new Date()));
    if (!date) return;
    const reason = window.prompt("Gerekçe (izin, tatil…):", "") ?? "";
    try {
      await appointmentService.saveException({ date, isClosed: true, reason });
      setExceptions(
        await appointmentService.exceptions(undefined, iso(new Date()), iso(new Date(Date.now() + 90 * 864e5))),
      );
      flash("Gün kapatıldı.");
      loadCalendar();
    } catch (e) {
      fail(e, "Gün kapatılamadı.");
    }
  };

  const removeException = async (id?: string) => {
    if (!id) return;
    try {
      await appointmentService.removeException(id);
      setExceptions((prev) => prev.filter((x) => x.availabilityExceptionId !== id));
      loadCalendar();
    } catch (e) {
      fail(e, "Silinemedi.");
    }
  };

  const saveType = async () => {
    if (!editType) return;
    try {
      await appointmentService.saveType(editType);
      setTypes(await appointmentService.types());
      setEditType(null);
      flash("Randevu türü kaydedildi.");
    } catch (e) {
      fail(e, "Tür kaydedilemedi.");
    }
  };

  const removeType = async (id?: string) => {
    if (!id || !window.confirm("Bu tür kaldırılsın mı? Geçmiş randevular silinmez.")) return;
    try {
      await appointmentService.removeType(id);
      setTypes(await appointmentService.types());
    } catch (e) {
      fail(e, "Tür kaldırılamadı.");
    }
  };

  const saveBlock = async () => {
    if (!blockDraft) return;
    try {
      await appointmentService.saveBlock({
        ...blockDraft,
        startAt: fromLocalInput(blockDraft.startAt),
        endAt: fromLocalInput(blockDraft.endAt),
      });
      setBlockDraft(null);
      flash("Meşguliyet takvime eklendi.");
      loadCalendar();
    } catch (e) {
      fail(e, "Eklenemedi.");
    }
  };

  const connectGoogle = async () => {
    try {
      // Aynı sekmede gidiyoruz: Google izin ekranı pop-up engelleyicilere takılır.
      window.location.href = await googleCalendarService.connectUrl();
    } catch (e) {
      fail(e, "Google bağlantısı başlatılamadı.");
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Google Takvim bağlantısı kaldırılsın mı? Yeni randevular için Meet bağlantısı üretilmez.")) return;
    try {
      await googleCalendarService.disconnect();
      setGoogle(await googleCalendarService.status());
      flash("Google bağlantısı kaldırıldı.");
    } catch (e) {
      fail(e, "Bağlantı kaldırılamadı.");
    }
  };

  const copyLink = async () => {
    if (!profile?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(profile.publicUrl);
      flash("Bağlantı kopyalandı.");
    } catch {
      setErr("Kopyalanamadı, bağlantıyı elle seçin.");
    }
  };

  if (loading) return <div className="ap-wrap"><div className="ap-empty">Yükleniyor…</div></div>;

  return (
    <div className="ap-wrap">
      <div className="ap-head">
        <div>
          <h1>Randevu ve Takvim</h1>
          <p>Dışarıdan gelen randevu talepleri, takviminiz ve müsaitlik ayarlarınız. Tüm saatler TSİ.</p>
        </div>
        <div className="ap-head-actions">
          <button
            className="ap-btn"
            onClick={() =>
              setBlockDraft({
                title: "",
                startAt: toLocalInput(new Date()),
                endAt: toLocalInput(new Date(Date.now() + 36e5)),
                isAllDay: false,
              })
            }
          >
            Meşguliyet ekle
          </button>
        </div>
      </div>

      {err && <div className="ap-note" style={{ borderLeftColor: "#b42318", color: "#b42318" }}>{err}</div>}
      {ok && <div className="ap-note" style={{ borderLeftColor: "#1b7a4b", color: "#1b7a4b" }}>{ok}</div>}

      <div className="ap-tabs">
        <button className={`ap-tab ${tab === "calendar" ? "on" : ""}`} onClick={() => setTab("calendar")}>
          Takvim
        </button>
        <button className={`ap-tab ${tab === "pending" ? "on" : ""}`} onClick={() => setTab("pending")}>
          Onay bekleyenler
          {pending.length > 0 && <span className="ap-pill s2">{pending.length}</span>}
        </button>
        <button className={`ap-tab ${tab === "hours" ? "on" : ""}`} onClick={() => setTab("hours")}>
          Randevu sayfam
        </button>
        {canSettings && (
          <button className={`ap-tab ${tab === "types" ? "on" : ""}`} onClick={() => setTab("types")}>
            Randevu türleri
          </button>
        )}
      </div>

      {/* ============ TAKVİM ============ */}
      {tab === "calendar" && (
        <>
          <div className="ap-nav">
            <button className="ap-btn sm" onClick={() => setWeekStart((d) => new Date(d.getTime() - 7 * 864e5))}>
              ‹ Önceki
            </button>
            <button className="ap-btn sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>
              Bu hafta
            </button>
            <button className="ap-btn sm" onClick={() => setWeekStart((d) => new Date(d.getTime() + 7 * 864e5))}>
              Sonraki ›
            </button>
            <span className="range">
              {dayLabel(weekDays[0])} – {dayLabel(weekDays[6])}
            </span>
            {people && (people.teamCount > 0 || people.canAll) && (
              <label className="ap-who">
                <span>Takvim</span>
                <select value={who} onChange={(e) => setWho(e.target.value)}>
                  <option value="me">Benim takvimim</option>
                  {people.teamCount > 0 && <option value="team">Ekibim ({people.teamCount} kişi + ben)</option>}
                  {people.people.filter((p) => p.relation === "team").length > 0 && <optgroup label="Ekibim">{people.people.filter((p) => p.relation === "team").map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}</optgroup>}
                  {people.canAll && people.people.filter((p) => p.relation === "all").length > 0 && <optgroup label="Diğer personel">{people.people.filter((p) => p.relation === "all").map((p) => <option key={p.userId} value={p.userId}>{p.name} · {p.role}</option>)}</optgroup>}
                </select>
              </label>
            )}
          </div>

          {/* Geniş ekran: saat ızgarası */}
          <div className="ap-cal">
            <div className="ap-cal-h" />
            {weekDays.map((d) => (
              <div key={iso(d)} className={`ap-cal-h ${iso(d) === iso(new Date()) ? "today" : ""}`}>
                {SHORT[d.getDay()]}
                <small>{d.getDate()}</small>
              </div>
            ))}
            {HOURS.map((h) => (
              <React.Fragment key={h}>
                <div className="ap-cal-hour">{String(h).padStart(2, "0")}:00</div>
                {weekDays.map((d) => (
                  <div key={iso(d) + h} className="ap-cal-cell">
                    {eventsAt(d, h).map((e) =>
                      e.kind === "apt" ? (
                        <button
                          key={e.a.appointmentId}
                          className={`ap-ev s${e.a.status}`}
                          onClick={() => setDetail(e.a)}
                          title={`${e.a.guestName} · ${e.a.typeName}${who !== "me" ? ` · ${e.a.staffName}` : ""}`}
                        >
                          <b>{hhmm(e.a.startAt)}{who !== "me" && <em className="ap-ev-who">{e.a.staffName}</em>}</b>
                          {e.a.guestName}
                        </button>
                      ) : (
                        <span key={e.b.calendarBlockId} className="ap-ev block">
                          <b>{hhmm(e.b.startAt)}{who !== "me" && e.b.staffName && <em className="ap-ev-who">{e.b.staffName}</em>}</b>
                          {e.b.title}
                        </span>
                      ),
                    )}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>

          {/* Dar ekran: ajanda */}
          <div className="ap-agenda ap-card">
            {weekDays.map((d) => {
              const evs = eventsFor(d);
              return (
                <div key={iso(d)} className="ap-agenda-day">
                  <div className={`d ${iso(d) === iso(new Date()) ? "today" : ""}`}>
                    {DAYS[d.getDay()]}, {dayLabel(d)}
                  </div>
                  {evs.length === 0 && <div className="none">Randevu yok</div>}
                  {evs.map((e) =>
                    e.kind === "apt" ? (
                      <button key={e.a.appointmentId} className={`ap-ev s${e.a.status}`} onClick={() => setDetail(e.a)}>
                        <b>
                          {hhmm(e.a.startAt)} – {hhmm(e.a.endAt)}
                        </b>
                        {who !== "me" ? `${e.a.staffName} · ` : ""}{e.a.guestName} · {e.a.typeName}
                      </button>
                    ) : (
                      <span key={e.b.calendarBlockId} className="ap-ev block">
                        <b>{hhmm(e.b.startAt)}</b>
                        {e.b.title}
                      </span>
                    ),
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ============ ONAY BEKLEYENLER ============ */}
      {tab === "pending" && (
        <div className="ap-card">
          <h2>Onay bekleyen talepler</h2>
          <p className="ap-sub">
            Her randevu sizin onayınızdan geçer. Onaylarsanız misafire takvim daveti gönderilir;
            reddederseniz gerekçeniz iletilir ve saat yeniden açılır.
          </p>
          {pending.length === 0 && <div className="ap-empty">Onay bekleyen talep yok.</div>}
          {pending.map((a) => (
            <div key={a.appointmentId} className="ap-row">
              <div className="ap-row-main">
                <div className="t">
                  {a.guestName}
                  {a.guestCompany ? ` · ${a.guestCompany}` : ""}
                </div>
                <div className="m">
                  {a.typeName} · {a.localRange} ·{" "}
                  {a.mode === 2 ? "Çevrim içi" : "Yüz yüze"} · {a.number}
                  <br />
                  {a.guestEmail}
                  {a.guestPhone ? ` · ${a.guestPhone}` : ""}
                  {a.guestNote ? (
                    <>
                      <br />
                      <i>“{a.guestNote}”</i>
                    </>
                  ) : null}
                </div>
              </div>
              {canUpdate && (
                <div className="ap-row-actions">
                  <button className="ap-btn primary sm" onClick={() => decide(a, true)}>
                    Onayla
                  </button>
                  <button className="ap-btn danger sm" onClick={() => decide(a, false)}>
                    Reddet
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ============ RANDEVU SAYFAM ============ */}
      {tab === "hours" && profile && (
        <>
          <div className="ap-card ap-setup">
            <h2>Kurulum</h2>
            <p className="ap-sub">
              Randevu almaya başlamak için aşağıdaki adımların tamamlanması gerekir.
              Adımlar sırayla yapılır; eksik olan varken bağlantınızı paylaşmayın.
            </p>
            <ol className="ap-steps">
              {setupSteps.map((s, i) => (
                <li key={i} className={s.done ? "done" : ""}>
                  <span className="mark" aria-hidden>
                    {s.done ? "✓" : i + 1}
                  </span>
                  <div>
                    <div className="t">{s.title}</div>
                    <div className="m">{s.done ? s.doneText : s.todo}</div>
                  </div>
                </li>
              ))}
            </ol>
            {setupSteps.every((s) => s.done) ? (
              <div className="ap-note" style={{ borderLeftColor: "#1b7a4b", color: "#14603a", marginBottom: 0 }}>
                Kurulum tamam. Bağlantınızı paylaşabilirsiniz — gelen talepler “Onay bekleyenler”
                sekmesine düşecek.
              </div>
            ) : (
              <div className="ap-note" style={{ marginBottom: 0 }}>
                Eksik adım varken sayfanız dışarıdan açılmaz ya da uygun saat görünmez.
              </div>
            )}
          </div>

          <div className="ap-card">
            <h2>Randevu sayfanız</h2>
            <p className="ap-sub">
              Bu bağlantıyı paylaştığınızda karşı taraf doğrudan sizin takviminizi görür, giriş
              yapması gerekmez. Yayında olması için hem burayı hem de Ayarlar’daki genel randevu
              anahtarını açmanız gerekir.
            </p>

            <label className="ap-switch" style={{ marginBottom: "0.875rem" }}>
              <input
                type="checkbox"
                checked={profile.isPublic}
                onChange={(e) => setProfile({ ...profile, isPublic: e.target.checked })}
              />
              <span className="track" />
              <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#3f4c63" }}>
                Sayfam yayında olsun
              </span>
            </label>

            {profile.publicUrl && (
              <div className="ap-link" style={{ marginBottom: "0.875rem" }}>
                <code>{profile.publicUrl}</code>
                <button className="ap-btn sm" onClick={copyLink}>
                  Kopyala
                </button>
              </div>
            )}

            <div className="ap-grid2">
              <div className="ap-field">
                <label>Bağlantı adı</label>
                <input
                  className="ap-input"
                  value={profile.slug}
                  onChange={(e) => setProfile({ ...profile, slug: e.target.value })}
                />
                <span className="hint">Adresin sonundaki kısım. Küçük harf ve tire.</span>
              </div>
              <div className="ap-field">
                <label>Görünen ünvan</label>
                <input
                  className="ap-input"
                  value={profile.displayTitle ?? ""}
                  onChange={(e) => setProfile({ ...profile, displayTitle: e.target.value })}
                  placeholder="Satış Sorumlusu"
                />
              </div>
            </div>

            <div className="ap-field">
              <label>Kısa tanıtım</label>
              <textarea
                className="ap-textarea"
                value={profile.intro ?? ""}
                onChange={(e) => setProfile({ ...profile, intro: e.target.value })}
                placeholder="Numune ve sipariş görüşmeleri için randevu alabilirsiniz."
              />
            </div>

            <div className="ap-grid2">
              <div className="ap-field">
                <label>Yüz yüze görüşme adresi</label>
                <input
                  className="ap-input"
                  value={profile.location ?? ""}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                />
              </div>
              <div className="ap-field">
                <label>Çevrim içi görüşme bağlantısı</label>
                <input
                  className="ap-input"
                  value={profile.defaultMeetingUrl ?? ""}
                  onChange={(e) => setProfile({ ...profile, defaultMeetingUrl: e.target.value })}
                  placeholder="https://meet.google.com/…"
                />
                <span className="hint">
                  {google?.connected
                    ? "Yedek oda. Google Takvim bağlı olduğu için her randevuya ayrı Meet bağlantısı üretilir; bu adres yalnız üretim başarısız olursa kullanılır."
                    : "Sabit odanız. Yalnız onayladığınız randevularda paylaşılır."}
                </span>
              </div>
            </div>

            <div className="ap-grid2">
              <div className="ap-field">
                <label>En erken kaç saat sonrası</label>
                <input
                  type="number"
                  className="ap-input"
                  value={profile.minNoticeHours}
                  onChange={(e) => setProfile({ ...profile, minNoticeHours: Number(e.target.value) })}
                />
                <span className="hint">Son dakika randevusunu engeller.</span>
              </div>
              <div className="ap-field">
                <label>En fazla kaç gün ilerisi</label>
                <input
                  type="number"
                  className="ap-input"
                  value={profile.maxAdvanceDays}
                  onChange={(e) => setProfile({ ...profile, maxAdvanceDays: Number(e.target.value) })}
                />
              </div>
              <div className="ap-field">
                <label>Saat aralığı (dk)</label>
                <input
                  type="number"
                  className="ap-input"
                  value={profile.slotStepMinutes}
                  onChange={(e) => setProfile({ ...profile, slotStepMinutes: Number(e.target.value) })}
                />
                <span className="hint">30 ise randevular 09:00, 09:30… diye başlar.</span>
              </div>
              <div className="ap-field">
                <label>Randevular arası boşluk (dk)</label>
                <input
                  type="number"
                  className="ap-input"
                  value={profile.bufferMinutes}
                  onChange={(e) => setProfile({ ...profile, bufferMinutes: Number(e.target.value) })}
                />
              </div>
            </div>

            <button className="ap-btn primary" onClick={saveProfile}>
              Sayfamı kaydet
            </button>
          </div>

          {google?.configured && (
            <div className="ap-card">
              <h2>Google Takvim</h2>
              <p className="ap-sub">
                Bağlarsanız onayladığınız her randevu kendi Google takviminize düşer ve
                görüşme için <b>o randevuya özel</b> bir Meet bağlantısı üretilir. Randevu
                iptal edilirse etkinlik takvimden de silinir.
              </p>

              {google.connected ? (
                <>
                  <div className="ap-row" style={{ borderBottom: 0, paddingTop: 0 }}>
                    <div className="ap-row-main">
                      <div className="t">
                        {google.googleEmail || "Bağlı hesap"}{" "}
                        <span className={`ap-pill ${google.lastError ? "s4" : "s3"}`}>
                          {google.lastError ? "Yenilenmeli" : "Bağlı"}
                        </span>
                      </div>
                      <div className="m">
                        {google.connectedAt &&
                          `Bağlantı: ${new Date(google.connectedAt).toLocaleDateString("tr-TR", { dateStyle: "long" })}`}
                        {google.lastSuccessAt &&
                          ` · Son başarılı işlem: ${new Date(google.lastSuccessAt).toLocaleDateString("tr-TR", { dateStyle: "long" })}`}
                      </div>
                    </div>
                    <div className="ap-row-actions">
                      <button className="ap-btn sm" onClick={connectGoogle}>
                        Yeniden bağlan
                      </button>
                      <button className="ap-btn danger sm" onClick={disconnectGoogle}>
                        Bağlantıyı kaldır
                      </button>
                    </div>
                  </div>
                  {google.lastError && (
                    <div className="ap-note" style={{ borderLeftColor: "#b42318", color: "#b42318", marginBottom: 0 }}>
                      {google.lastError}
                    </div>
                  )}
                </>
              ) : (
                <button className="ap-btn primary" onClick={connectGoogle}>
                  Google Takvim’e bağlan
                </button>
              )}
            </div>
          )}

          <div className="ap-card">
            <h2>Çalışma saatleriniz</h2>
            <p className="ap-sub">
              Kapalı bıraktığınız günlerde randevu alınamaz. Öğle arası için günü kapatmak yerine
              saatleri daraltın; bu ekran gün başına tek aralık tutar.
            </p>
            {DAYS.map((name, dow) => {
              const row = hours.find((h) => h.dayOfWeek === dow);
              return (
                <div key={dow} className={`ap-day ${row ? "" : "off"}`}>
                  <label className="ap-switch" style={{ width: "9.5rem", flex: "none" }}>
                    <input type="checkbox" checked={!!row} onChange={(e) => setDayHours(dow, e.target.checked)} />
                    <span className="track" />
                    <span className="name" style={{ width: "auto" }}>
                      {name}
                    </span>
                  </label>
                  <div className="times">
                    <input
                      type="time"
                      className="ap-input"
                      value={row?.startTime ?? "09:00"}
                      onChange={(e) => patchDay(dow, { startTime: e.target.value })}
                    />
                    <span style={{ color: "#8f9bb0" }}>–</span>
                    <input
                      type="time"
                      className="ap-input"
                      value={row?.endTime ?? "18:00"}
                      onChange={(e) => patchDay(dow, { endTime: e.target.value })}
                    />
                  </div>
                </div>
              );
            })}
            <button className="ap-btn primary" style={{ marginTop: "0.875rem" }} onClick={saveHours}>
              Saatleri kaydet
            </button>
          </div>

          <div className="ap-card">
            <h2>Kapalı günler</h2>
            <p className="ap-sub">İzin ve tatil günleri haftalık şablonunuzu ezer.</p>
            {exceptions.length === 0 && <div className="ap-empty">Kapalı gün yok.</div>}
            {exceptions.map((x) => (
              <div key={x.availabilityExceptionId} className="ap-row">
                <div className="ap-row-main">
                  <div className="t">{new Date(x.date).toLocaleDateString("tr-TR", { dateStyle: "long" })}</div>
                  <div className="m">{x.isClosed ? "Tüm gün kapalı" : `${x.startTime} – ${x.endTime}`}{x.reason ? ` · ${x.reason}` : ""}</div>
                </div>
                <div className="ap-row-actions">
                  <button className="ap-btn danger sm" onClick={() => removeException(x.availabilityExceptionId)}>
                    Kaldır
                  </button>
                </div>
              </div>
            ))}
            <button className="ap-btn" style={{ marginTop: "0.875rem" }} onClick={addException}>
              Gün kapat
            </button>
          </div>
        </>
      )}

      {/* ============ RANDEVU TÜRLERİ ============ */}
      {tab === "types" && canSettings && (
        <div className="ap-card">
          <h2>Randevu türleri</h2>
          <p className="ap-sub">
            Dışarıdaki kişi süre seçmez, tür seçer. Süre ve görüşme şekli türden gelir.
            Bir türü hangi çalışanların karşıladığını burada belirlersiniz.
          </p>
          {types.length === 0 && <div className="ap-empty">Henüz tür tanımlanmamış.</div>}
          {types.map((t) => (
            <div key={t.appointmentTypeId} className="ap-row">
              <div className="ap-row-main">
                <div className="t">
                  {t.name}{" "}
                  {!t.isPublic && <span className="ap-pill s1">Yalnız içeride</span>}
                </div>
                <div className="m">
                  {t.durationMinutes} dk ·{" "}
                  {t.modeOption === 1 ? "Yüz yüze" : t.modeOption === 2 ? "Çevrim içi" : "Yüz yüze veya çevrim içi"} ·{" "}
                  {t.roleIds.length} rol
                  {t.description ? ` · ${t.description}` : ""}
                </div>
              </div>
              <div className="ap-row-actions">
                <button className="ap-btn sm" onClick={() => setEditType({ ...t })}>
                  Düzenle
                </button>
                <button className="ap-btn danger sm" onClick={() => removeType(t.appointmentTypeId)}>
                  Kaldır
                </button>
              </div>
            </div>
          ))}
          <button
            className="ap-btn primary"
            style={{ marginTop: "0.875rem" }}
            onClick={() =>
              setEditType({
                name: "",
                durationMinutes: 30,
                modeOption: 3,
                isPublic: true,
                sortOrder: types.length,
                roleIds: [],
              })
            }
          >
            Yeni tür
          </button>
        </div>
      )}

      {/* ============ RANDEVU DETAYI ============ */}
      {detail && (
        <div className="ap-modal-bg" onClick={() => setDetail(null)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{detail.guestName}</h3>
            <p className="ap-sub">
              {detail.number} · <span className={`ap-pill s${detail.status}`}>{detail.statusName}</span>
            </p>

            <div className="ap-field">
              <label>Konu</label>
              <div style={{ fontSize: "0.8125rem" }}>{detail.typeName}</div>
            </div>
            <div className="ap-field">
              <label>Tarih ve saat</label>
              <div style={{ fontSize: "0.8125rem" }}>
                {new Date(detail.startAt).toLocaleDateString("tr-TR", { dateStyle: "long" })} · {detail.localRange}
              </div>
            </div>
            <div className="ap-field">
              <label>Görüşme şekli</label>
              <div style={{ fontSize: "0.8125rem" }}>
                {detail.mode === 2 ? "Çevrim içi" : "Yüz yüze"}
                {detail.mode === 2 && detail.meetingUrl ? ` · ${detail.meetingUrl}` : ""}
                {detail.mode === 1 && detail.location ? ` · ${detail.location}` : ""}
              </div>
            </div>
            <div className="ap-field">
              <label>İletişim</label>
              <div style={{ fontSize: "0.8125rem" }}>
                {detail.guestEmail}
                {detail.guestPhone ? ` · ${detail.guestPhone}` : ""}
                {detail.guestCompany ? ` · ${detail.guestCompany}` : ""}
                {detail.firmName ? ` · kayıtlı firma: ${detail.firmName}` : ""}
              </div>
            </div>
            {detail.guestNote && (
              <div className="ap-field">
                <label>Not</label>
                <div style={{ fontSize: "0.8125rem" }}>{detail.guestNote}</div>
              </div>
            )}
            {detail.cancelReason && (
              <div className="ap-field">
                <label>Gerekçe</label>
                <div style={{ fontSize: "0.8125rem" }}>{detail.cancelReason}</div>
              </div>
            )}

            <div className="foot">
              {canUpdate && detail.status === 2 && (
                <>
                  <button className="ap-btn danger" onClick={() => decide(detail, false)}>
                    Reddet
                  </button>
                  <button className="ap-btn primary" onClick={() => decide(detail, true)}>
                    Onayla
                  </button>
                </>
              )}
              {canUpdate && detail.status === 3 && (
                <>
                  <button className="ap-btn danger" onClick={() => cancelAppointment(detail)}>
                    İptal et
                  </button>
                  <button className="ap-btn" onClick={() => markStatus(detail, 7)}>
                    Gelinmedi
                  </button>
                  <button className="ap-btn primary" onClick={() => markStatus(detail, 6)}>
                    Tamamlandı
                  </button>
                </>
              )}
              <button className="ap-btn" onClick={() => setDetail(null)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ TÜR DÜZENLE ============ */}
      {editType && (
        <div className="ap-modal-bg" onClick={() => setEditType(null)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editType.appointmentTypeId ? "Randevu türü" : "Yeni randevu türü"}</h3>
            <p className="ap-sub">Süre ve görüşme şekli bu türden gelir.</p>

            <div className="ap-field">
              <label>Ad</label>
              <input
                className="ap-input"
                value={editType.name}
                onChange={(e) => setEditType({ ...editType, name: e.target.value })}
                placeholder="Numune görüşmesi"
              />
            </div>
            <div className="ap-field">
              <label>Açıklama</label>
              <textarea
                className="ap-textarea"
                value={editType.description ?? ""}
                onChange={(e) => setEditType({ ...editType, description: e.target.value })}
              />
            </div>
            <div className="ap-grid2">
              <div className="ap-field">
                <label>Süre (dakika)</label>
                <input
                  type="number"
                  className="ap-input"
                  value={editType.durationMinutes}
                  onChange={(e) => setEditType({ ...editType, durationMinutes: Number(e.target.value) })}
                />
              </div>
              <div className="ap-field">
                <label>Görüşme şekli</label>
                <select
                  className="ap-select"
                  value={editType.modeOption}
                  onChange={(e) =>
                    setEditType({ ...editType, modeOption: Number(e.target.value) as 1 | 2 | 3 })
                  }
                >
                  <option value={3}>Yüz yüze veya çevrim içi</option>
                  <option value={1}>Yalnız yüz yüze</option>
                  <option value={2}>Yalnız çevrim içi</option>
                </select>
              </div>
            </div>

            <label className="ap-switch" style={{ marginBottom: "0.875rem" }}>
              <input
                type="checkbox"
                checked={editType.isPublic}
                onChange={(e) => setEditType({ ...editType, isPublic: e.target.checked })}
              />
              <span className="track" />
              <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#3f4c63" }}>
                Genel randevu sayfasında görünsün
              </span>
            </label>

            <div className="ap-field">
              <label>Bu türü karşılayan roller</label>
              <div style={{ maxHeight: "12rem", overflowY: "auto", border: "0.0625rem solid #dce4ef", borderRadius: "0.375rem", padding: "0.5rem" }}>
                {roles.length === 0 && <div className="hint">Rol listesi alınamadı.</div>}
                {roles.map((r) => {
                  const on = editType.roleIds.includes(r.roleId);
                  return (
                    <label
                      key={r.roleId}
                      style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.25rem 0", fontSize: "0.8125rem", cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) =>
                          setEditType({
                            ...editType,
                            roleIds: e.target.checked
                              ? [...editType.roleIds, r.roleId]
                              : editType.roleIds.filter((x) => x !== r.roleId),
                          })
                        }
                      />
                      {r.name}
                      {typeof r.userCount === "number" && (
                        <span style={{ color: "#8f9bb0" }}>· {r.userCount} kişi</span>
                      )}
                    </label>
                  );
                })}
              </div>
              <span className="hint">
                Bu roldeki herkes bu türü karşılar. Kişinin randevuya açık olup olmadığı ayrı:
                Randevu sayfam sekmesindeki “Sayfam yayında olsun” anahtarı belirler.
              </span>
            </div>

            <div className="foot">
              <button className="ap-btn" onClick={() => setEditType(null)}>
                Vazgeç
              </button>
              <button className="ap-btn primary" onClick={saveType} disabled={!editType.name.trim()}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MEŞGULİYET ============ */}
      {blockDraft && (
        <div className="ap-modal-bg" onClick={() => setBlockDraft(null)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Meşguliyet ekle</h3>
            <p className="ap-sub">
              Bu aralık randevuya kapanır. Dışarıdaki kişi yalnız saatin dolu olduğunu görür,
              başlığı görmez.
            </p>
            <div className="ap-field">
              <label>Başlık</label>
              <input
                className="ap-input"
                value={blockDraft.title}
                onChange={(e) => setBlockDraft({ ...blockDraft, title: e.target.value })}
                placeholder="Fabrika turu"
              />
            </div>
            <div className="ap-grid2">
              <div className="ap-field">
                <label>Başlangıç</label>
                <input
                  type="datetime-local"
                  className="ap-input"
                  value={blockDraft.startAt.slice(0, 16)}
                  onChange={(e) => setBlockDraft({ ...blockDraft, startAt: e.target.value })}
                />
              </div>
              <div className="ap-field">
                <label>Bitiş</label>
                <input
                  type="datetime-local"
                  className="ap-input"
                  value={blockDraft.endAt.slice(0, 16)}
                  onChange={(e) => setBlockDraft({ ...blockDraft, endAt: e.target.value })}
                />
              </div>
            </div>
            <div className="foot">
              <button className="ap-btn" onClick={() => setBlockDraft(null)}>
                Vazgeç
              </button>
              <button className="ap-btn primary" onClick={saveBlock} disabled={!blockDraft.title.trim()}>
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
